import { useMemo, useState } from 'react';
import { Button, Empty, Select, Space, Tag, Typography } from 'antd';
import { useSelect } from '@refinedev/antd';
import type { IERPUser, IRole } from '@admin-panel/types';
import { readRoleOverride, writeRoleOverride } from '@admin-panel/access-control';
import SettingsSection from './SettingsSection';

type AdminGeneralSettingsProps = {
  user?: IERPUser;
};

const isLocalEnv = import.meta.env['VITE_APP_ENV'] === 'local';

const AdminGeneralSettings = ({ user }: AdminGeneralSettingsProps) => {
  const { selectProps } = useSelect<IRole>({
    resource: 'roles',
    optionLabel: 'name',
    optionValue: 'name',
    pagination: {
      pageSize: 50,
    },
    queryOptions: {
      enabled: isLocalEnv,
    },
    onSearch: value => [
      {
        field: 'name',
        operator: 'startswith',
        value,
      },
    ],
  });
  const userRoles = useMemo(() => (user?.roles ? user.roles.map(role => role.name) : []), [user]);
  const overrideRoles = useMemo(() => readRoleOverride(), []);
  const [draftRoles, setDraftRoles] = useState<Array<string> | null>(null);

  const hasOverride = overrideRoles !== null;
  const effectiveRoles = hasOverride ? overrideRoles : userRoles;
  const selectedRoles = draftRoles ?? effectiveRoles;

  if (!isLocalEnv) {
    return (
      <SettingsSection title="">
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No settings available yet." />
      </SettingsSection>
    );
  }

  const applyOverride = () => {
    writeRoleOverride(selectedRoles);
    window.location.reload();
  };

  const clearOverride = () => {
    writeRoleOverride(null);
    window.location.reload();
  };

  return (
    <SettingsSection
      title="Role Debugger"
      description="Local-only override to simulate permissions without API calls."
    >
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Space>
          <Typography.Text type="secondary">Override status:</Typography.Text>
          {hasOverride ? <Tag color="green">Active</Tag> : <Tag>Inactive</Tag>}
        </Space>

        <div>
          <Typography.Text type="secondary">Effective roles:</Typography.Text>
          <div>
            {effectiveRoles && effectiveRoles.length > 0 ? (
              <Space size={[6, 6]} wrap>
                {effectiveRoles.map(role => (
                  <Tag key={role}>{role}</Tag>
                ))}
              </Space>
            ) : (
              <Typography.Text type="secondary">None</Typography.Text>
            )}
          </div>
        </div>

        <div>
          <Typography.Text type="secondary">Set override roles:</Typography.Text>
          <Select
            mode="multiple"
            allowClear
            showSearch
            placeholder="Select roles to simulate"
            style={{ width: '100%' }}
            value={selectedRoles}
            onChange={value => setDraftRoles(value)}
            options={selectProps.options}
            loading={selectProps.loading}
            onSearch={selectProps.onSearch}
            filterOption={false}
          />
        </div>

        <Space>
          <Button type="primary" onClick={applyOverride}>
            Apply override
          </Button>
          <Button onClick={clearOverride} disabled={!hasOverride}>
            Clear override
          </Button>
        </Space>
      </Space>
    </SettingsSection>
  );
};

export default AdminGeneralSettings;
